import { DEFAULT_REGION } from "../constants";

/**
 * Utility class for building AWS console URLs
 */
export class AwsUrlBuilder {
  private static readonly ARN_REGION_INDEX = 3;
  private static readonly STATE_MACHINE_ARN_PREFIX = "arn:aws:states:";
  private static readonly STATE_MACHINE_ARN_SEGMENT = ":stateMachine:";

  /**
   * Extract region from an ARN
   */
  static extractRegion(arn: string): string {
    const parts = arn.split(":");
    return parts[this.ARN_REGION_INDEX] || DEFAULT_REGION;
  }

  /**
   * Build Step Functions execution console URL
   */
  static buildStepFunctionsUrl(executionArn: string): string {
    const region = this.extractRegion(executionArn);
    const encodedArn = encodeURIComponent(executionArn);
    return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/v2/executions/details/${encodedArn}`;
  }

  /**
   * Determine whether an ARN identifies a Step Functions state machine
   * (e.g. arn:aws:states:{region}:{account}:stateMachine:{name}). Uses plain
   * string checks (no regex) so it is safe against malformed input.
   */
  static isStateMachineArn(arn: string): boolean {
    return arn.startsWith(this.STATE_MACHINE_ARN_PREFIX) && arn.includes(this.STATE_MACHINE_ARN_SEGMENT);
  }

  /**
   * Extract a human-readable resource name from an ARN: the segment after the
   * last ":" and then after the last "/". Falls back to the raw ARN when it
   * cannot be parsed further, and returns "" for empty input.
   */
  static extractResourceName(arn: string): string {
    if (!arn) {
      return "";
    }
    const afterColon = arn.substring(arn.lastIndexOf(":") + 1);
    const afterSlash = afterColon.substring(afterColon.lastIndexOf("/") + 1);
    return afterSlash || arn;
  }

  /**
   * Build a Step Functions console URL for a state-machine ARN (the
   * state-machine view, not an execution view). Region is derived from the ARN.
   */
  static buildStateMachineUrl(stateMachineArn: string): string {
    const region = this.extractRegion(stateMachineArn);
    const encodedArn = encodeURIComponent(stateMachineArn);
    return `https://${region}.console.aws.amazon.com/states/home?region=${region}#/statemachines/view/${encodedArn}`;
  }
}
